import type { WidgetEventTypesMapping } from '@sunflower-games/analytics-events';
import dayjs from 'dayjs';

import type { Benefit, EventTriggerPopupType } from '../stores/event-trigger/types';
import { isMessageCtaPopupType, OfferMode } from '../stores/event-trigger/types';

export const isOfferPopupType = (benefit: Benefit<EventTriggerPopupType>): benefit is Benefit<'offer'> =>
  benefit.benefitType === 'offer';

export const isGiftPopupType = (benefit: Benefit<EventTriggerPopupType>): benefit is Benefit<'gift'> =>
  benefit.benefitType === 'gift';

export const isMultiPackageGiftPopupType = (
  benefit: Benefit<EventTriggerPopupType>,
): benefit is Benefit<'multiPackageGift'> => benefit.benefitType === 'multiPackageGift';

type EventTriggerBenefitAnalyticsProps = Pick<WidgetEventTypesMapping['event_trigger'], 'benefit_type'>;

// Analytics must not see the new benefitType: a multi-package gift reports as a gift.
export const getBenefitTypeAnalyticsProps = (benefitType: string): EventTriggerBenefitAnalyticsProps => ({
  benefit_type: (benefitType === 'multiPackageGift'
    ? 'gift'
    : benefitType) as EventTriggerBenefitAnalyticsProps['benefit_type'],
});

// Every gift reports how many packages it shows: '1' for a regular gift, the package count for a multi-package one.
export const getGiftPackageCountAnalyticsProps = (benefit: Benefit<EventTriggerPopupType>) => {
  if (isGiftPopupType(benefit)) return { event_type: 'gift_package_count' as const, event_value: '1' };
  if (isMultiPackageGiftPopupType(benefit)) {
    return { event_type: 'gift_package_count' as const, event_value: String(benefit.packages.length) };
  }
  return {};
};

export const isPersonalGiftPopupType = (benefit: Benefit<EventTriggerPopupType>): benefit is Benefit<'personalGift'> =>
  benefit.benefitType === 'personalGift';

export const isMessagePopupType = (benefit: Benefit<EventTriggerPopupType>): benefit is Benefit<'message'> =>
  benefit.benefitType === 'message';

export const isMetagameCtaMessage = (benefit: Benefit<EventTriggerPopupType>): benefit is Benefit<'message'> =>
  isMessagePopupType(benefit) && benefit.ctaType === 'METAGAME';

// Checks the legacy ctaType/ctaValue (what the app reads) and every ctas entry. Raw, unvalidated responses come
// through here, so a malformed ctas must not throw.
export const hasUnsupportedPopupCta = (benefit: Benefit<EventTriggerPopupType>): boolean => {
  if (!isMessagePopupType(benefit)) return false;
  const ctas: ({ type?: unknown; value?: unknown } | null)[] = [
    { type: benefit.ctaType, value: benefit.ctaValue },
    ...(Array.isArray(benefit.ctas) ? benefit.ctas : []),
  ];
  return ctas.some((cta) => cta?.type === 'POPUP' && !isMessageCtaPopupType(cta.value));
};

export const isRoadPopupType = (benefit: Benefit<EventTriggerPopupType>): benefit is Benefit<'road'> =>
  benefit.benefitType === 'road';

export const isOfferV2PopupType = (benefit: Benefit<EventTriggerPopupType>): benefit is Benefit<'2-3-offer'> =>
  benefit.benefitType === '2-3-offer';

export const isRollingOfferPopupType = (benefit: Benefit<EventTriggerPopupType>): benefit is Benefit<'rollingOffer'> =>
  benefit.benefitType === 'rollingOffer';

export const isTakeOrLoseBenefit = (benefit: Benefit<EventTriggerPopupType>): boolean =>
  isOfferV2PopupType(benefit) && benefit.mode === OfferMode.TAKE_OR_LOSE;

export const isLimitedOfferBenefit = (benefit: Benefit<EventTriggerPopupType>): boolean =>
  isOfferV2PopupType(benefit) && benefit.mode === OfferMode.LIMITED;

// An offer benefit that can render as a lobby widget — the widget shows miniImageUrl, so offers without one are skipped.
// Rolling offers are classified separately via isRollingOfferPopupType.
export const isOfferWidgetBenefit = (benefit: Benefit<EventTriggerPopupType>): boolean =>
  (isOfferPopupType(benefit) || isOfferV2PopupType(benefit)) && !!benefit.miniImageUrl;

export const isCoinBackBoosterEventTrigger = (
  benefit: Benefit<EventTriggerPopupType>,
): benefit is Benefit<'coinBackBooster'> => benefit.benefitType === 'coinBackBooster';

export const isQuestEventTrigger = (benefit: Benefit<EventTriggerPopupType>): benefit is Benefit<'quest'> =>
  benefit.benefitType === 'quest';

export const isActionEventTrigger = (benefit: Benefit<EventTriggerPopupType>): benefit is Benefit<'action'> =>
  benefit.benefitType === 'action';

export const isRoadEventTrigger = (benefit: Benefit<EventTriggerPopupType>): benefit is Benefit<'road'> =>
  benefit.benefitType === 'road';

export const isRaffleBenefit = (
  benefit: Benefit<EventTriggerPopupType>,
): benefit is (Benefit<'message'> | Benefit<'gift'> | Benefit<'multiPackageGift'> | Benefit<'personalGift'>) & {
  raffleId: string;
} =>
  (isMessagePopupType(benefit) ||
    isGiftPopupType(benefit) ||
    isMultiPackageGiftPopupType(benefit) ||
    isPersonalGiftPopupType(benefit)) &&
  !!benefit.raffleId;

export const isWithinRewardClaimWindow = (
  expiredDate: Date | string | undefined,
  rewardClaimEndDate: Date | string | undefined | null,
): boolean => {
  return dayjs().isAfter(expiredDate) && dayjs().isBefore(dayjs(rewardClaimEndDate));
};
